import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { FinalConfirmationComponent } from './pages/seller/car-form/components/final-confirmation/final-confirmation.component';
import { AdminGuard } from './shared/guards/admin.guard';
import { BuyerGuard } from './shared/guards/buyer.guard';
import { NoAuthGuard } from './shared/guards/no-auth.guard';
import { SellerGuard } from './shared/guards/seller.guard';

const routes: Routes = [
    { path: '', redirectTo: '/login', pathMatch: 'full' },
    {
        path: 'signup',
        loadChildren: () => import('./pages/authentication/signup/signup.module').then((m) => m.SignupPageModule),
        canActivate: [NoAuthGuard]
    },
    {
        path: 'login',
        loadChildren: () => import('./pages/authentication/login/login.module').then((m) => m.LoginPageModule),
        canActivate: [NoAuthGuard]
    },
    {
        path: 'reset-password',
        loadChildren: () =>
            import('./pages/authentication/reset-password/reset-password.module').then((m) => m.ResetPasswordPageModule)
    },
    {
        path: 'reset-password-confirmation',
        loadChildren: () =>
            import('./pages/authentication/reset-password-confirmation/reset-password-confirmation.module').then(
                (m) => m.ResetPasswordConfirmationPageModule
            )
    },
    {
        path: 'seller-menu',
        loadChildren: () => import('./pages/seller/main-menu/main-menu.module').then((m) => m.MainMenuPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'form/new',
        loadChildren: () => import('./pages/seller/car-form/car-form.module').then((m) => m.CarFormPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'form/:uid',
        loadChildren: () => import('./pages/seller/car-form/car-form.module').then((m) => m.CarFormPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'value',
        loadChildren: () => import('./pages/seller/car-form/car-form.module').then((m) => m.CarFormPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'dealer-preferences',
        loadChildren: () => import('./pages/preferences/preferences.module').then((m) => m.PreferencesPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'seller-preferences',
        loadChildren: () => import('./pages/preferences/preferences.module').then((m) => m.PreferencesPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'your-listings',
        loadChildren: () => import('./pages/seller/your-listings/your-listings.module').then((m) => m.YourListingsPageModule),
        canActivate: [SellerGuard]
    },
    // Dealership pages
    {
        path: 'dealership-menu',
        loadChildren: () =>
            import('./pages/dealership/dealership-menu/dealership-menu.module').then((m) => m.DealershipMenuPageModule),
        canActivate: [BuyerGuard]
    },

    {
        path: 'condition-report/:uid',
        loadChildren: () =>
            import('./shared/components/vehicle/pages/condition-report/condition-report.module').then(
                (m) => m.ConditionReportPageModule
            )
    },
    {
        path: 'admin-panel',
        loadChildren: () => import('./pages/admin-panel/admin-panel.module').then((m) => m.AdminPanelPageModule),
        canActivate: [AdminGuard]
    },
    {
        path: 'landing',
        loadChildren: () => import('./pages/landing/landing.module').then((m) => m.LandingPageModule)
    },
    {
        path: 'contact-us',
        loadChildren: () => import('./pages/contact-us/contact-us.module').then((m) => m.ContactUsPageModule)
    },
    {
        path: 'congratulations',
        component: FinalConfirmationComponent
    },
    {
        path: 'bid-history/:uid',
        loadChildren: () =>
            import('./shared/components/vehicle/pages/bid-history/bid-history.module').then((m) => m.BidHistoryPageModule)
    },
    {
        path: 'wrong-navigation',
        loadChildren: () =>
            import('./pages/authentication/wrong-navigation/wrong-navigation.module').then((m) => m.WrongNavigationPageModule)
    },

    // Auctions Pages
    {
        path: 'auctions',
        loadChildren: () => import('./pages/dealership/auctions/auctions.module').then((m) => m.AuctionsPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'favorites',
        loadChildren: () => import('./pages/dealership/auctions/auctions.module').then((m) => m.AuctionsPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'recent',
        loadChildren: () => import('./pages/dealership/auctions/auctions.module').then((m) => m.AuctionsPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'bidding',
        loadChildren: () => import('./pages/dealership/auctions/auctions.module').then((m) => m.AuctionsPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'upcoming',
        loadChildren: () => import('./pages/dealership/auctions/auctions.module').then((m) => m.AuctionsPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'won',
        loadChildren: () => import('./pages/dealership/auctions/auctions.module').then((m) => m.AuctionsPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'closed',
        loadChildren: () => import('./pages/dealership/auctions/auctions.module').then((m) => m.AuctionsPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'auctions/:auid',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'auctions/:auid/confirm',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'auctions/:auid/contact',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'auctions/:auid/images',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [BuyerGuard]
    },
    {
        path: 'vehicles/:vuid',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'vehicles/:vuid/confirm',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'vehicles/:vuid/images',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [SellerGuard]
    },
    {
        path: 'vehicles/:vuid/contact',
        loadChildren: () => import('./pages/auction/auction.module').then((m) => m.AuctionPageModule),
        canActivate: [SellerGuard]
    },

    {
        path: 'bid-history',
        loadChildren: () =>
            import('./shared/components/vehicle/pages/bid-history/bid-history.module').then((m) => m.BidHistoryPageModule)
    },
    {
        path: 'condition-report',
        loadChildren: () =>
            import('./shared/components/vehicle/pages/condition-report/condition-report.module').then(
                (m) => m.ConditionReportPageModule
            )
    },
    {
        path: '**',
        redirectTo: 'wrong-navigation'
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
    exports: [RouterModule]
})
export class AppRoutingModule {}
